<template>
    <header
        class="chat_list_header"
        :class="{
            'chat_list_header--collapsed': collapsed,
            'chat_list_header--menu-open': menuOpen
        }"
    >
        <div class="chat_list_header__main">
            <!-- Teams and Groups -->
            <button
                type="button"
                class="chat_header_action chat_header_action--primary"
                :class="{
                    'chat_header_action--locked': !canCreateGroup
                }"
                :aria-label="groupButtonLabel"
                :title="groupButtonLabel"
                @click="handleGroupAction"
            >
                <span class="chat_header_action__icon" aria-hidden="true" v-if="collapsed">
                    <svg
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                        focusable="false"
                    >
                        <path
                            fill="currentColor"
                            d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3
                               -3 1.34-3 3 1.34 3 3 3zm-8 0c1.66
                               0 3-1.34 3-3S9.66 5 8 5 5 6.34
                               5 8s1.34 3 3 3zm0 2c-2.33 0-7
                               1.17-7 3.5V19h14v-2.5C15
                               14.17 10.33 13 8 13zm8 0c-.29
                               0-.62.02-.97.05 1.16.84 1.97
                               1.97 1.97 3.45V19h6v-2.5c0-2.33
                               -4.67-3.5-7-3.5z"
                        />
                    </svg>
                </span>

                <span
                    v-if="!collapsed"
                    class="chat_header_action__label"
                >
                    Groups
                </span>

                <!-- <span
                    v-if="!canCreateGroup"
                    class="chat_header_action__lock"
                    aria-hidden="true"
                >
                    <i class="fa fa-lock"></i>
                </span> -->
            </button>

            <div
                v-if="!collapsed"
                class="chat_list_header__actions"
            >
                <!-- Desktop actions -->
                <template v-if="!isMobile">
                    <button
                        type="button"
                        class="chat_header_action"
                        title="Activity tracker"
                        @click="$emit('open-tracker')"
                    >
                        <!-- <i
                            class="fa fa-line-chart chat_header_action__small-icon"
                            aria-hidden="true"
                        ></i> -->

                        <span class="chat_header_action__label">
                            Tracker
                        </span>
                    </button>

                    <router-link
                        :to="{ name: 'saChatRequestList' }"
                        class="chat_header_action"
                        title="Connection requests"
                    >
                        <!-- <i
                            class="fa fa-user-plus chat_header_action__small-icon"
                            aria-hidden="true"
                        ></i> -->

                        <span class="chat_header_action__label">
                            Requests
                        </span>

                        <span
                            v-if="requestCount > 0"
                            class="chat_header_badge"
                        >
                            {{ formattedRequestCount }}
                        </span>
                    </router-link>
                </template>

                <!-- Mobile tools -->
                <button
                    v-else
                    type="button"
                    class="chat_header_action"
                    title="Open chat tools"
                    @click="$emit('open-mobile-tools')"
                >
                    <!-- <i
                        class="fa fa-sliders chat_header_action__small-icon"
                        aria-hidden="true"
                    ></i> -->

                    <span class="chat_header_action__label">
                        Tools
                    </span>
                </button>

                <self-user-status />
            </div>

            <!-- More menu -->
            <div 
                v-if="!collapsed"
                v-click-outside="closeMenu"
                class="chat_header_menu"
            >
                <button
                    type="button"
                    class="chat_header_more_btn"
                    :aria-expanded="String(menuOpen)"
                    aria-label="Open chat menu"
                    @click.stop="toggleMenu"
                >
                    <span></span>
                    <span></span>
                    <span></span>
                </button>

                <transition name="chat-menu">
                    <ul
                        v-if="menuOpen"
                        class="chat_header_dropdown"
                    >     
                        <li v-if="!isGroupChat">
                            <button
                                class="chat_header__block"
                                :title="isBlocked ? 'User is blocked' : 'Block user'"
                                @click="handleBlockClick"
                            >
                                <svg
                                    class="chat_header__block_icon"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 2a8 8 0 0 1 6.32 12.9L7.1 5.68A7.96 7.96 0 0 1 12 4zM5.68 7.1 16.9 18.32A8 8 0 0 1 5.68 7.1z"
                                    />
                                </svg>

                                <span class="chat_header__block_text">
                                    Block User
                                </span>
                            </button>
                        </li>

                        <li>
                            <router-link
                                :to="{ name: 'userBlockList' }"
                                @click.native="closeMenu"
                            >
                                <!-- <i
                                    class="fa fa-ban"
                                    aria-hidden="true"
                                ></i> -->

                                <span>Block List</span>
                            </router-link>
                        </li>

                        <li v-if="isMobile || collapsed">
                            <router-link
                                :to="{ name: 'saChatRequestList' }"
                                @click.native="closeMenu"
                            >
                                <!-- <i
                                    class="fa fa-user-plus"
                                    aria-hidden="true"
                                ></i> -->

                                <span>Requests</span>

                                <span
                                    v-if="requestCount > 0"
                                    class="chat_header_badge"
                                >
                                    {{ formattedRequestCount }}
                                </span>
                            </router-link>
                        </li>
                    </ul>
                </transition>
            </div>
        </div>
    </header>
</template>

<script>
import { bus } from '@/main';

const SelfUserStatus = () => import(
    '@/components/private/workspace/self-user-status'
);

function firstValue(values) {
    return values.find((value) => {
        return (
            value !== undefined &&
            value !== null &&
            String(value).trim() !== ''
        );
    });
}

function toBoolean(value) {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value === 1;
    }

    if (typeof value === 'string') {
        return ['1', 'true', 'yes', 'online'].indexOf(
            value.trim().toLowerCase()
        ) !== -1;
    }

    return false;
}
export default {
    name: 'ChatListHeader',

    components: {
        SelfUserStatus,
    },

    props: {
        selectedRoomInfo: {
            type: Object,
            default: null
        },
        canCreateGroup: {
            type: Boolean,
            default: false
        },

        connectionRequestCount: {
            type: Object,
            default: null
        },

        isMobile: {
            type: Boolean,
            default: false
        },

        collapsed: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            menuOpen: false,
        };
    },

    computed: {
        room() {
            return this.selectedRoomInfo || {};
        },

        roomUser() {
            return (
                this.room.user ||
                {}
            );
        },

        isGroupChat() {
            return Boolean(
                toBoolean(this.room.is_group_chat)
            );
        },

        roomUserId() {
            return firstValue([
                this.roomUser.id,
                this.roomUser.user_id,
                this.room.user_id
            ]);
        },

        isPinned() {
            return toBoolean(
                firstValue([
                    this.room.pinned,
                    this.room.is_pinned
                ])
            );
        },

        isBlocked() {
            return toBoolean(
                firstValue([
                    this.room.blocked,
                    this.room.is_blocked,
                    this.roomUser.blocked,
                    this.roomUser.is_blocked
                ])
            );
        },
        requestCount() {
            const count = this.connectionRequestCount &&
                this.connectionRequestCount.connection_request_count;

            const normalizedCount = Number(count);

            return Number.isFinite(normalizedCount)
                ? Math.max(normalizedCount, 0)
                : 0;
        },

        formattedRequestCount() {
            return this.requestCount > 99
                ? '99+'
                : String(this.requestCount);
        },

        groupButtonLabel() {
            return this.canCreateGroup
                ? 'Create Teams and Groups'
                : 'Upgrade your package to create Teams and Groups';
        }
    },


    beforeDestroy() {
        this.menuOpen = false;
    },

    methods: {
        handleBlockClick() {
            if (
                this.isBlocked ||
                !this.roomUserId
            ) {
                return;
            }

            this.$emit(
                'block',
                this.roomUserId,
                this.roomUser.first_name || '',
                this.roomUser.last_name || ''
            );
        },
        handleGroupAction() {
            this.closeMenu();

            if (this.canCreateGroup) {
                this.$emit('create-group');
                return;
            }

            this.$emit('show-package-access');
        },

        toggleMenu() {
            this.menuOpen = !this.menuOpen;
        },

        closeMenu() {
            this.menuOpen = false;
        }
    }
};
</script>

<style lang="scss" scoped>
.chat_list_header {
    position: relative;
    z-index: 20;
    padding: 10px 12px;
    background: #111111;
    // border-bottom: 1px solid rgba(255, 255, 255, 0.01);

    &__main {
        display: flex;
        align-items: center;
        min-width: 0;
        gap: 7px;
    }

    &__actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex: 1;
        min-width: 0;
        gap: 7px;
    }

    &--collapsed {
        padding: 10px 8px;

        .chat_list_header__main {
            justify-content: center;
            flex-wrap: wrap;
        }

        .chat_header_action--primary {
            width: 44px;
            min-width: 44px;
            padding: 0;
        }

        .chat_header_menu {
            margin-left: 0;
        }

        .chat_header_dropdown {
            right: -7px;
        }
    }
}

.chat_header_action {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 29px;
    padding: 0 14px;
    gap: 7px;
    overflow: visible;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 19px;
    background: rgba(255, 255, 255, 0.09);
    color: #ffd400;
    font-family: "MyriadProRegular", "Lato", sans-serif;
    font-size: 15px;
    line-height: 1;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    box-shadow: none;
    outline: none;
    transition:
        background-color 0.2s ease,
        border-color 0.2s ease,
        color 0.2s ease,
        transform 0.2s ease;
    letter-spacing: 0.03em;

    &:hover,
    &:focus-visible {
        border-color: rgba(255, 212, 0, 0.55);
        background: rgba(255, 212, 0, 0.12);
        color: #ffffff;
        text-decoration: none;
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }

    &--primary {
        flex-shrink: 0;
        font-weight: 600;
        font-family: 'AceSansDemo';
        border-color: #ff4338;
        color: #fff;
        // font-size: 17px;
        // min-height: 36px;
        // border-radius: 10px;
        // letter-spacing: 1px;
        // border-color: #ff4338;
        // color: #ffffff;

        &:hover,
        &:focus-visible {
            border-color: #ffd400;
            background: #ffd400;
            color: #000000;
        }
    }

    &--locked {
        border-color: rgba(255, 255, 255, 0.14);
        background: rgba(255, 255, 255, 0.08);
        color: rgba(255, 255, 255, 0.58);

        &:hover,
        &:focus-visible {
            border-color: rgba(255, 212, 0, 0.45);
            background: rgba(255, 212, 0, 0.08);
            color: #ffd400;
        }
    }

    &__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    &__small-icon {
        font-size: 14px;
    }

    &__label {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__lock {
        display: inline-flex;
        align-items: center;
        font-size: 11px;
    }
}

.chat_header_badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: #cf231e;
    color: #ffffff;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
}

.chat_header_menu {
    position: relative;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin-left: 2px;
}

.chat_header_more_btn {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    padding: 0;
    gap: 3px;
    border: 1px solid transparent;
    border-radius: 50%;
    background: transparent;
    cursor: pointer;
    outline: none;
    transition:
        background-color 0.2s ease,
        border-color 0.2s ease;

    span {
        display: block;
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: #d9d9d9;
        transition: background-color 0.2s ease;
    }

    &:hover,
    &:focus-visible {
        border-color: rgba(255, 255, 255, 0.1);
        background: rgba(255, 255, 255, 0.08);

        span {
            background: #ffd400;
        }
    }
}

.chat_list_header--menu-open {
    .chat_header_more_btn {
        background: rgba(255, 212, 0, 0.1);

        span {
            background: #ffd400;
        }
    }
}

.chat_header_dropdown {
    position: absolute;
    top: calc(100% + 9px);
    right: 0;
    z-index: 100;
    width: 165px;
    margin: 0;
    padding: 6px;
    list-style: none;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 8px;
    background: #1b1b1b;
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.4);

    &::before {
        position: absolute;
        top: -6px;
        right: 13px;
        width: 10px;
        height: 10px;
        content: "";
        border-top: 1px solid rgba(255, 255, 255, 0.16);
        border-left: 1px solid rgba(255, 255, 255, 0.16);
        background: #1b1b1b;
        transform: rotate(45deg);
    }

    li {
        position: relative;
        z-index: 1;
    }

    a {
        display: flex;
        align-items: center;
        padding: 6px 10px;
        gap: 9px;
        border-radius: 5px;
        color: #e7e7e7;
        font-size: 16px;
        text-decoration: none;
        transition:
            background-color 0.2s ease,
            color 0.2s ease;

        i {
            width: 16px;
            color: #ffd400;
            text-align: center;
        }

        .chat_header_badge {
            margin-left: auto;
        }

        &:hover,
        &:focus-visible {
            background: rgba(255, 212, 0, 0.1);
            color: #ffd400;
        }
    }
}

.chat-menu-enter-active,
.chat-menu-leave-active {
    transition:
        opacity 0.16s ease,
        transform 0.16s ease;
    transform-origin: top right;
}

.chat-menu-enter,
.chat-menu-leave-to {
    opacity: 0;
    transform: translateY(-5px) scale(0.97);
}

.chat_header__block {
    padding: 6px 10px;
    gap: 6px;
    border-radius: 5px;
    color: #e7e7e7;
    font-size: 16px;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    width: 100%;

    &:hover:not(:disabled) {
        color: #b80531;
        background: #ea00381a;
    }

    .chat_header__block_icon{
        width: 18px;
        height: 18px;
        fill: currentColor;
    }
    .chat_header__block_text{line-height: 1;}
}
@media only screen and (max-width: 1359px) {
    .chat_header_action {
        padding: 0 10px;
        font-size: 13px;

        &--primary {
            min-width: auto;
        }
    }

    .chat_header_more_btn {
        width: 36px;
        height: 36px;
    }
}

@media only screen and (max-width: 900px) {
    .chat_list_header {
        padding: 9px 10px;
    }

    .chat_header_action {
        padding: 0 9px;

        &__label {
            max-width: 75px;
        }

        &--primary {
            min-width: 126px;

            .chat_header_action__label {
                max-width: 90px;
            }
        }
    }
}

@media only screen and (max-width: 480px) {
    .chat_list_header__main {
        gap: 6px;
    }

    .chat_list_header__actions {
        gap: 5px;
    }

    .chat_header_action {
        padding: 0 18px;

        &--primary {
            min-width: 44px;
        }
    }
}
</style>